// Quick-add's natural-language parse (artboard 1a).
//
// "Check beam calc for 320 Hall Lane fri p1 @Abdul"
//   title     Check beam calc
//   project   320 Hall Lane      due Fri 29 Aug      priority 1      owner Abdul
//
// Pure: no DOM, no Firestore. The overlay renders whatever this returns and
// writes nothing the caller did not confirm.
//
// TOKENS ARE MATCHED RIGHT-TO-LEFT FROM THE END OF THE LINE, and the title is
// whatever remains at the front (HANDOFF §1, "Parsing rules"). That order is
// the whole trick: it means a title may contain a word that looks like a token
// — "Friday briefing pack fri" gives the title "Friday briefing pack" and a due
// date, because only the trailing "fri" is ever read as one.
//
// A token becomes a chip ONLY when it is unambiguous. Two projects matching
// "paddock" is not a 50/50 guess to be made on someone's behalf; it is a
// question, and the overlay asks it.
//
// It never returns plannedDate. Quick-add sets a DUE date and says so; the day
// a person commits to is theirs to choose in the planner.

const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const DAY_ABBR = { sun: 0, mon: 1, tue: 2, tues: 2, wed: 3, weds: 3, thu: 4, thur: 4, thurs: 4, fri: 5, sat: 6 };
// Words that end a title only because a token followed them.
const DANGLING = /\s+(for|on|at|to|with|in|by|of|from|re|about|-|–|—)$/i;

const pad = (n) => String(n).padStart(2, '0');
const key = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** The next occurrence of a weekday, today included — "fri" said on a Friday
 *  means today, not a week away. */
export function nextWeekday(index, from = new Date()) {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  d.setDate(d.getDate() + ((index - d.getDay() + 7) % 7));
  return key(d);
}

/** 'Fri 29 Aug' — the chip's own label. */
export function dueLabel(dayString) {
  if (!dayString) return '';
  const d = new Date(dayString + 'T00:00:00');
  const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()];
  return `${day} ${d.getDate()} ${mon}`;
}

function matchTrailing(word, ctx) {
  const w = word.toLowerCase();
  const day = DAYS.indexOf(w) >= 0 ? DAYS.indexOf(w)
    : Object.prototype.hasOwnProperty.call(DAY_ABBR, w) ? DAY_ABBR[w] : -1;
  if (day >= 0) return { kind: 'due', value: nextWeekday(day, ctx.today) };
  if (w === 'today') return { kind: 'due', value: key(ctx.today) };
  if (w === 'tomorrow' || w === 'tmw') {
    const d = new Date(ctx.today); d.setDate(d.getDate() + 1);
    return { kind: 'due', value: key(d) };
  }
  let m = /^p([123])$/.exec(w);
  if (m) return { kind: 'priority', value: Number(m[1]) };
  // 2h, 1.5h, 90m — an estimate, never a duration in the title
  m = /^(\d+(?:\.\d+)?)(h|hr|hrs)$/.exec(w);
  if (m) return { kind: 'estimate', value: Number(m[1]) };
  m = /^(\d+)(m|min|mins)$/.exec(w);
  if (m) return { kind: 'estimate', value: Math.round((Number(m[1]) / 60) * 100) / 100 };
  if (w.startsWith('@') && w.length > 1) {
    const q = w.slice(1);
    const hits = (ctx.people || []).filter((p) =>
      String(p.id).toLowerCase() === q
      || String(p.name || '').toLowerCase().split(/\s+/)[0] === q
      || String(p.name || '').toLowerCase().replace(/\s+/g, '') === q);
    if (hits.length === 1) return { kind: 'owner', value: hits[0] };
    if (hits.length > 1) return { kind: 'owner', ambiguous: q, matches: hits };
    return null;   // unknown name: leave it in the title rather than eat it
  }
  return null;
}

/** Name, reference or postcode — the three things a person might type
 *  (HANDOFF §1). Case- and space-insensitive on the reference so "P2291",
 *  "p-2291" and "P-2291" are one thing. */
function projectHits(phrase, projects) {
  const q = phrase.trim().toLowerCase();
  if (q.length < 3) return [];
  const flat = (s) => String(s || '').toLowerCase().replace(/[\s-]/g, '');
  const fq = flat(q);
  return (projects || []).filter((p) =>
    String(p.name || '').toLowerCase().includes(q)
    || flat(p.ref).includes(fq)
    || flat(p.number).includes(fq)
    || flat(p.postcode) === fq);
}

/**
 * @param {string} text  the raw line
 * @param {object} ctx   { projects: [{id,name,ref,number,postcode,sector,state}],
 *                         people: [{id,name,email}], today: Date }
 */
export function parseTaskLine(text, ctx = {}) {
  const today = ctx.today || new Date();
  const c = { ...ctx, today };
  const raw = String(text || '');
  const out = {
    raw, title: '', spans: [],
    project: null, ambiguous: null,
    dueDate: null, priority: null, owner: null, estimateHours: null,
  };
  if (!raw.trim()) return out;

  // 1. Eat trailing single-word tokens, right to left.
  let end = raw.length;
  for (;;) {
    const head = raw.slice(0, end).replace(/\s+$/, '');
    const at = head.lastIndexOf(' ');
    const word = head.slice(at + 1);
    if (!word) break;
    const hit = matchTrailing(word, c);
    if (!hit) break;
    const start = at + 1;
    if (hit.kind === 'due' && out.dueDate == null) out.dueDate = hit.value;
    else if (hit.kind === 'priority' && out.priority == null) out.priority = hit.value;
    else if (hit.kind === 'estimate' && out.estimateHours == null) out.estimateHours = hit.value;
    else if (hit.kind === 'owner' && out.owner == null && !hit.ambiguous) out.owner = hit.value;
    else if (hit.kind === 'owner' && hit.ambiguous) out.ambiguous = { token: word, kind: 'owner', matches: hit.matches };
    else break;   // a second token of the same kind: stop, it belongs to the title
    out.spans.push({ start, end: start + word.length, kind: hit.kind });
    end = start;
  }

  // 2. From what is left, take the longest trailing phrase that names a project.
  //    Longest first, so "Paddock Farm Barns" wins over "Paddock".
  let head = raw.slice(0, end).replace(/\s+$/, '');
  const words = head.split(/\s+/).filter(Boolean);
  for (let take = Math.min(6, words.length); take >= 1; take--) {
    const phrase = words.slice(words.length - take).join(' ');
    const hits = projectHits(phrase, c.projects);
    if (!hits.length) continue;
    const start = head.length - phrase.length;
    if (hits.length === 1) {
      out.project = hits[0];
      out.spans.push({ start, end: head.length, kind: 'project' });
    } else {
      out.ambiguous = { token: phrase, kind: 'project', matches: hits };
      out.spans.push({ start, end: head.length, kind: 'ambiguous' });
    }
    head = head.slice(0, start).replace(/\s+$/, '');
    break;
  }

  // 3. Whatever is in front is the title — minus a preposition left dangling by
  //    the token that followed it, so "Check beam calc for <project>" reads as
  //    "Check beam calc" rather than trailing a stray "for".
  out.title = head.replace(DANGLING, '').trim();
  out.spans.sort((a, b) => a.start - b.start);
  return out;
}

/** The chips the overlay draws, in the canvas's order: PROJECT, DUE, PRIORITY,
 *  OWNER, then the unfilled ESTIMATE. */
export function chipsOf(parsed) {
  const chips = [];
  if (parsed.project) chips.push({ kind: 'project', label: 'PROJECT', value: parsed.project.name });
  if (parsed.dueDate) chips.push({ kind: 'due', label: 'DUE', value: dueLabel(parsed.dueDate) });
  if (parsed.priority) chips.push({ kind: 'priority', label: 'PRIORITY', value: String(parsed.priority), dot: parsed.priority });
  if (parsed.owner) chips.push({ kind: 'owner', label: 'OWNER', value: parsed.owner.name || parsed.owner.id });
  chips.push({ kind: 'estimate', label: 'ESTIMATE', value: parsed.estimateHours ? `${parsed.estimateHours}h` : 'add',
    empty: !parsed.estimateHours });
  return chips;
}
